import React, { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAssignDialerSeatMutation, useSetAddOnMutation } from "@/store/rest_api";
import { AddOnType, DialerSeatLine } from "@/model/interface/AddOn";
import type Membership from "@/model/interface/Membership";
import { DialerSeat } from "@/enum/DialerSeat";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { cn } from "@/lib/utils";
import { formatAddOnPrice, renewingSeats } from "@/util/addOns";
import { apiErrorMessage } from "@/util/apiError";
import { memberName } from "@/util/member";

interface PowerDialerDialogProps {
  single: DialerSeatLine;
  multi: DialerSeatLine;
  members: Membership[];
  canAssign: boolean;
  endDate: string | null;
  onClose: () => void;
}

interface SeatStepperProps {
  label: string;
  hint: string;
  seats: number;
  minimum: number;
  changed: boolean;
  onChange: (seats: number) => void;
}

function SeatStepper({ label, hint, seats, minimum, changed, onChange }: SeatStepperProps) {
  return (
    <div className={cn("flex items-center justify-between gap-4 rounded-lg border p-3", changed && "border-primary bg-primary/5")}>
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="flex shrink-0 items-center rounded-lg border bg-background">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={`Remove a ${label.toLowerCase()} seat`}
          disabled={seats <= minimum}
          onClick={() => onChange(seats - 1)}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-8 text-center text-sm font-semibold tabular-nums">{seats}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={`Add a ${label.toLowerCase()} seat`}
          onClick={() => onChange(seats + 1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function memberLabel(member: Membership): string {
  return memberName(member, member.user.email);
}

export function PowerDialerDialog({ single, multi, members, canAssign, endDate, onClose }: PowerDialerDialogProps) {
  const [setAddOn, { isLoading: isSaving }] = useSetAddOnMutation();
  const [assignDialerSeat, { isLoading: isAssigning }] = useAssignDialerSeatMutation();
  const [singleSeats, setSingleSeats] = useState(renewingSeats(single));
  const [multiSeats, setMultiSeats] = useState(renewingSeats(multi));
  const [confirmingTurnOff, setConfirmingTurnOff] = useState(false);

  const renewingSingle = renewingSeats(single);
  const renewingMulti = renewingSeats(multi);
  const hasSeats = single.quantity + multi.quantity > 0;
  const isRenewing = renewingSingle + renewingMulti > 0;
  const isEnding = hasSeats && !isRenewing;
  const changed = singleSeats !== renewingSingle || multiSeats !== renewingMulti;
  const costChange = (singleSeats - renewingSingle) * single.priceCents + (multiSeats - renewingMulti) * multi.priceCents;
  const newTotal = singleSeats * single.priceCents + multiSeats * multi.priceCents;
  const singleFull = single.assigned >= single.quantity;
  const multiFull = multi.assigned >= multi.quantity;
  const endsOn = endDate ?? "your billing date";
  const removingSeats = singleSeats < renewingSingle || multiSeats < renewingMulti;
  const totalLine = newTotal > 0 ? ` New total ${formatAddOnPrice(newTotal)} a month.` : "";

  const seatHint = (line: DialerSeatLine, reach: string) =>
    [
      line.priceCents === 0 ? `Free, ${reach}` : `${formatAddOnPrice(line.priceCents)} a seat a month, ${reach}`,
      line.assigned > 0 ? `${line.assigned} assigned` : null,
    ]
      .filter(Boolean)
      .join(", ");

  const changeSummary = () => {
    if (singleSeats + multiSeats === 0) {
      return `The power dialer stops renewing after ${endsOn}.`;
    }
    if (costChange > 0) {
      return `+${formatAddOnPrice(costChange)} a month, prorated onto your next invoice.${totalLine}`;
    }
    if (!removingSeats) {
      return `New seats start right away.${totalLine}`;
    }
    return `Removed seats give no credit and can be added back free until ${endsOn}.${totalLine}`;
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const saveSeats = async () => {
    const changes = [
      { addOnType: AddOnType.POWER_DIALER_SINGLE, quantity: singleSeats, current: renewingSingle },
      { addOnType: AddOnType.POWER_DIALER_MULTI, quantity: multiSeats, current: renewingMulti },
    ].filter((change) => change.quantity !== change.current);
    try {
      for (const change of changes) {
        await setAddOn({ addOnType: change.addOnType, quantity: change.quantity }).unwrap();
      }
      toast.success(hasSeats ? "Power dialer seats updated" : "Power dialer added");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update seats"));
    }
  };

  const keepPowerDialer = async () => {
    const lines = [
      { addOnType: AddOnType.POWER_DIALER_SINGLE, quantity: single.quantity },
      { addOnType: AddOnType.POWER_DIALER_MULTI, quantity: multi.quantity },
    ].filter((line) => line.quantity > 0);
    try {
      for (const line of lines) {
        await setAddOn({ addOnType: line.addOnType, quantity: line.quantity }).unwrap();
      }
      toast.success("The power dialer will keep renewing");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to keep the power dialer"));
    }
  };

  const turnOff = async () => {
    const lines = [
      { addOnType: AddOnType.POWER_DIALER_SINGLE, current: renewingSingle },
      { addOnType: AddOnType.POWER_DIALER_MULTI, current: renewingMulti },
    ].filter((line) => line.current > 0);
    try {
      for (const line of lines) {
        await setAddOn({ addOnType: line.addOnType, quantity: 0 }).unwrap();
      }
      toast.success(`The power dialer stops renewing after ${endsOn}`);
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to turn off the power dialer"));
    }
  };

  const assignSeat = async (membershipId: string, seat: DialerSeat) => {
    try {
      await assignDialerSeat({ id: membershipId, data: { dialerSeat: seat } }).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to assign the seat"));
    }
  };

  useEffect(() => {
    setSingleSeats(renewingSingle);
    setMultiSeats(renewingMulti);
  }, [renewingSingle, renewingMulti]);

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto text-foreground sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Power dialer</DialogTitle>
          <DialogDescription>Each member who runs the power dialer needs a seat.</DialogDescription>
        </DialogHeader>

        {isEnding ? (
          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="font-semibold">{endDate ? `Ends ${endDate}` : "Ends at your billing date"}</p>
            <p className="text-muted-foreground">
              Your seats keep working until then. Keep the power dialer to carry on with no new charge.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Seats</p>
            <SeatStepper
              label="Single line"
              hint={seatHint(single, "dials one line at a time")}
              seats={singleSeats}
              minimum={single.assigned}
              changed={singleSeats !== renewingSingle}
              onChange={setSingleSeats}
            />
            <SeatStepper
              label="Multi-line"
              hint={seatHint(multi, "dials up to 3 lines")}
              seats={multiSeats}
              minimum={multi.assigned}
              changed={multiSeats !== renewingMulti}
              onChange={setMultiSeats}
            />
            {changed && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted p-3">
                <p className="text-sm tabular-nums">{changeSummary()}</p>
                <Button size="sm" onClick={saveSeats} disabled={isSaving}>
                  {hasSeats ? "Save seats" : "Add power dialer"}
                </Button>
              </div>
            )}
          </div>
        )}

        {hasSeats && (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Who has a seat</p>
            <div className="divide-y rounded-lg border">
              {members.map((member) => {
                const seat = member.dialerSeat ?? DialerSeat.NONE;
                return (
                  <div key={member.id} className="flex items-center justify-between gap-4 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{memberLabel(member)}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {member.status === MembershipStatus.PENDING ? `Invited, ${member.user.email}` : member.user.email}
                      </p>
                    </div>
                    <Select
                      value={seat}
                      onValueChange={(value) => assignSeat(member.id, value as DialerSeat)}
                      disabled={!canAssign || isAssigning}
                    >
                      <SelectTrigger className="w-40 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DialerSeat.NONE}>No seat</SelectItem>
                        <SelectItem value={DialerSeat.SINGLE_LINE} disabled={seat !== DialerSeat.SINGLE_LINE && singleFull}>
                          Single line
                        </SelectItem>
                        <SelectItem value={DialerSeat.MULTI_LINE} disabled={seat !== DialerSeat.MULTI_LINE && multiFull}>
                          Multi-line
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
            {!canAssign && <p className="text-xs text-muted-foreground">Only owners and admins can assign seats.</p>}
          </div>
        )}

        {confirmingTurnOff ? (
          <div className="space-y-3 rounded-lg border border-destructive/40 p-3">
            <p className="text-sm">
              The power dialer keeps working until {endsOn}, then it won't renew. Removed seats give no credit.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmingTurnOff(false)}>
                Keep it
              </Button>
              <Button variant="destructive" size="sm" onClick={turnOff} disabled={isSaving}>
                Turn off power dialer
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            {isRenewing ? (
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setConfirmingTurnOff(true)}
              >
                Turn off power dialer
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              {isEnding && (
                <Button onClick={keepPowerDialer} disabled={isSaving}>
                  Keep power dialer
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
